/**
 * Payments Module - TanStack Query Hooks
 *
 * This module provides all hooks for payment-related operations including:
 * - Payment cards management (CRUD)
 * - Payment history retrieval
 *
 * All hooks use centralized query keys from queryKeys.ts
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CreditCardItem } from "js/components/Memberships/types";
import { PaginationTable } from "js/components/types";

import {
  createCard,
  deleteCart,
  getCards,
  getPaymentHistory,
  updateCard,
} from "../api/payments";
import { toast } from "../utils/toast";
import { queryKeys } from "./queryKeys";

// ============================================================================
// QUERY HOOKS (GET)
// ============================================================================

/**
 * Get all payment cards
 * @returns Payment cards with loading and error states
 */
export const useGetCards = () => {
  return useQuery({
    queryKey: queryKeys.payments.cards(),
    queryFn: getCards,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Get payment history with pagination
 * @param pagination - Pagination parameters (current page, page size)
 * @returns Payment history with loading and error states
 */
export const useGetPaymentHistory = (pagination: PaginationTable) => {
  return useQuery({
    queryKey: queryKeys.payments.history(pagination),
    queryFn: () => getPaymentHistory(pagination),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

// ============================================================================
// MUTATION HOOKS (CREATE, UPDATE, DELETE)
// ============================================================================

/**
 * Create a new payment card
 * Invalidates: payment cards, membership details
 */
export const useCreateCard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: CreditCardItem) => createCard(values),
    onSuccess: () => {
      toast.success("Tarjeta agregada exitosamente");
      // Invalidate payment cards list
      queryClient.invalidateQueries({ queryKey: queryKeys.payments.cards() });
      // Invalidate membership as payment method may affect it
      queryClient.invalidateQueries({ queryKey: queryKeys.membership.all });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al agregar la tarjeta";
      toast.error(errorMessage);
    },
  });
};

/**
 * Update a payment card (set as default)
 * Invalidates: payment cards, membership details
 */
export const useUpdateCard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cardId: string) => updateCard(cardId),
    onSuccess: () => {
      toast.success("Tarjeta actualizada exitosamente");
      // Invalidate payment cards list
      queryClient.invalidateQueries({ queryKey: queryKeys.payments.cards() });
      // Invalidate membership as default payment method may affect it
      queryClient.invalidateQueries({ queryKey: queryKeys.membership.all });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al actualizar la tarjeta";
      toast.error(errorMessage);
    },
  });
};

/**
 * Delete a payment card
 * Invalidates: payment cards, membership details
 */
export const useDeleteCard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cardId: string) => deleteCart(cardId),
    onSuccess: () => {
      toast.success("Tarjeta eliminada exitosamente");
      // Invalidate payment cards list
      queryClient.invalidateQueries({ queryKey: queryKeys.payments.cards() });
      // Invalidate membership as available payment methods changed
      queryClient.invalidateQueries({ queryKey: queryKeys.membership.all });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al eliminar la tarjeta";
      toast.error(errorMessage);
    },
  });
};
